import { apiUrl } from '@monorepo/shared/apiClient/httpClient';
import { DocumentFileNameCell } from '@monorepo/shared/componentsV2/documents/DocumentFileNameCell';
import { FolderNameCell } from '@monorepo/shared/componentsV2/documents/FolderNameCell';
import {
  DocumentsTable,
  ExpandedFolderPlaceholderText,
} from '@monorepo/shared/componentsV2/documents/styled';
import {
  composeDocumentsFolderRow,
  getDocumentsRowCreatedAt,
  getDocumentsRowCreatedByName,
  getFileByDocumentsRowIdMap,
} from '@monorepo/shared/documents/composeDocumentsTableRows';
import {
  DOCUMENTS_TABLE_ROW_KIND,
  EXPANDED_FOLDER_PLACEHOLDER_LABELS,
} from '@monorepo/shared/documents/consts';
import { DocumentsTableRow } from '@monorepo/shared/documents/types';
import { Loading } from '@monorepo/shared/componentsV2/Loading';
import { useSkipWidgetLink } from '@monorepo/shared/componentsV2/SkipWidgetLink';
import { EmptyState } from '@monorepo/shared/componentsV2/EmptyState';
import { Pagination } from '@monorepo/shared/componentsV2/Table/Pagination';
import { TableColumn } from '@monorepo/shared/componentsV2/Table/types';
import { useMovableTableRows } from '@monorepo/shared/componentsV2/Table/useMovableTableRows';
import { DocumentFolderWithFileCountResponse } from 'mapistry-shared';
import { useInlineFolderExpansion } from '@monorepo/shared/hooks/documents/useInlineFolderExpansion';
import { useFrontendPagination } from '@monorepo/shared/hooks/useFrontendPagination';
import { useHasOrganizationUpdatePermissions } from '@monorepo/shared/hooks/permissions/useHasPermissions';
import { useToast } from '@monorepo/shared/contexts/ToastContext';
import {
  DOCUMENT_FOLDER_SCOPE_LABELS,
  isOrganizationScopedFolder,
} from '@monorepo/shared/utils/documentFolderScope';
import { lookFor } from '@monorepo/shared/utils/lookFor';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useDocumentFoldersList } from '../../hooks/useDocumentFoldersList';
import { useOrganizationDocumentFilesList } from '../../hooks/useOrganizationDocumentFilesList';
import { useUpdateOrganizationDocument } from '../../hooks/useUpdateOrganizationDocument';
import { StyledMain } from '../styled';
import { FolderDocumentsTableActions } from '../FolderDocumentsPage/FolderDocumentsTableActions';
import { FoldersTableActions } from './FoldersTableActions';

interface FoldersTableProps {
  filter: string;
  organizationId: string;
  scrollToFolderId?: string | null;
  onScrollToFolderHandled?: () => void;
}

function getColumns(
  organizationId: string,
  canEditOrgSettings: boolean,
  expandedFolderIds: Set<string>,
  toggleFolder: (folderId: string) => void,
  hasExpandableFolders: boolean,
): TableColumn<DocumentsTableRow>[] {
  const columns: TableColumn<DocumentsTableRow>[] = [
    {
      id: 'name',
      header: 'Folder Name',
      contents: (row) => {
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.PLACEHOLDER) {
          return (
            <ExpandedFolderPlaceholderText>
              {EXPANDED_FOLDER_PLACEHOLDER_LABELS[row.placeholder]}
            </ExpandedFolderPlaceholderText>
          );
        }
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) {
          return (
            <DocumentFileNameCell
              name={row.file.name}
              downloadUrl={`${apiUrl}${row.file.downloadUrl}`}
              indented
              isDraggable={canEditOrgSettings}
            />
          );
        }
        const isExpandable = isOrganizationScopedFolder(row.folder.scope);
        return (
          <FolderNameCell
            name={row.folder.name}
            to={row.folder.id}
            isExpandable={isExpandable}
            isExpanded={expandedFolderIds.has(row.folder.id)}
            onToggleExpand={() => toggleFolder(row.folder.id)}
            showOrganizationScopedIcon={isExpandable}
            showChevronSlot={hasExpandableFolders}
          />
        );
      },
    },
    {
      id: 'scope',
      header: 'Content Management',
      contents: (row) =>
        row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER
          ? DOCUMENT_FOLDER_SCOPE_LABELS[row.folder.scope]
          : '',
      align: 'center',
      width: '15%',
    },
    {
      id: 'createdAt',
      header: 'Date Added',
      contents: getDocumentsRowCreatedAt,
      align: 'center',
      width: '20%',
    },
    {
      id: 'createdBy',
      header: 'Created By',
      contents: getDocumentsRowCreatedByName,
      align: 'center',
      width: '15%',
    },
  ];
  if (canEditOrgSettings) {
    columns.push({
      id: 'actions',
      header: 'Actions',
      contents: (row) => {
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER) {
          return (
            <FoldersTableActions
              organizationId={organizationId}
              folder={row.folder}
            />
          );
        }
        if (row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE) {
          return (
            <FolderDocumentsTableActions
              organizationId={organizationId}
              document={row.file}
            />
          );
        }
        return '';
      },
      align: 'center',
      width: '10%',
    });
  }
  return columns;
}

export function FoldersTable({
  filter,
  organizationId,
  scrollToFolderId,
  onScrollToFolderHandled,
}: FoldersTableProps) {
  const { documentFolders, isLoading } = useDocumentFoldersList({
    organizationId,
  });
  const { documentFiles, isLoading: isLoadingFiles } =
    useOrganizationDocumentFilesList({ organizationId });
  const canEditOrgSettings =
    useHasOrganizationUpdatePermissions(organizationId);

  const {
    expandedFolderIds,
    folderIdsWithMatchingFiles,
    toggleFolder,
    composeDisplayRows,
  } = useInlineFolderExpansion({
    organizationId,
    files: documentFiles,
    searchTerm: filter,
  });

  const hasExpandableFolders = useMemo(
    () =>
      documentFolders?.some((folder) =>
        isOrganizationScopedFolder(folder.scope),
      ) ?? false,
    [documentFolders],
  );

  const columns = useMemo(
    () =>
      getColumns(
        organizationId,
        !!canEditOrgSettings,
        expandedFolderIds,
        toggleFolder,
        hasExpandableFolders,
      ),
    [
      canEditOrgSettings,
      organizationId,
      expandedFolderIds,
      toggleFolder,
      hasExpandableFolders,
    ],
  );

  const filterRowsBy = useCallback(
    (row: DocumentFolderWithFileCountResponse) =>
      folderIdsWithMatchingFiles.has(row.id) || lookFor(filter, row, ['name']),
    [filter, folderIdsWithMatchingFiles],
  );

  const { page, setPage, filteredResults, rows, rowsPerPage } =
    useFrontendPagination({
      items: documentFolders,
      filterRowsBy,
      resetPageWhen: [filter, organizationId],
    });

  const displayRows = useMemo(
    () => composeDisplayRows(rows.map(composeDocumentsFolderRow)),
    [composeDisplayRows, rows],
  );

  useEffect(() => {
    if (!scrollToFolderId) return;

    const indexInFilteredResults = filteredResults.findIndex(
      (folder) => folder.id === scrollToFolderId,
    );
    if (indexInFilteredResults !== -1) {
      setPage(Math.floor(indexInFilteredResults / rowsPerPage));
      onScrollToFolderHandled?.();
      return;
    }

    const folderExistsButIsFilteredOut = documentFolders?.some(
      (folder) => folder.id === scrollToFolderId,
    );
    if (folderExistsButIsFilteredOut) {
      onScrollToFolderHandled?.();
    }
  }, [
    documentFolders,
    filteredResults,
    onScrollToFolderHandled,
    rowsPerPage,
    scrollToFolderId,
    setPage,
  ]);

  const { showUserFriendlyErrorToast, success } = useToast();
  const [moveDocument] = useUpdateOrganizationDocument({
    config: { throwOnError: true },
  });

  const fileByRowId = useMemo(
    () => getFileByDocumentsRowIdMap(displayRows),
    [displayRows],
  );

  const handleMoveFile = useCallback(
    async (rowId: string, folderId: string | null) => {
      const file = fileByRowId.get(rowId);
      if (!file || folderId == null || file.folderId === folderId) return;
      try {
        await moveDocument({
          organizationId,
          documentId: file.id,
          document: { folderId },
        });
        success('Document was moved.');
      } catch (err) {
        showUserFriendlyErrorToast(err, 'Unable to move document.');
      }
    },
    [
      fileByRowId,
      moveDocument,
      organizationId,
      showUserFriendlyErrorToast,
      success,
    ],
  );

  const isRowDraggable = useCallback(
    (row: DocumentsTableRow) =>
      !!canEditOrgSettings && row.kind === DOCUMENTS_TABLE_ROW_KIND.FILE,
    [canEditOrgSettings],
  );

  const isRowDropTarget = useCallback(
    (row: DocumentsTableRow, draggedRowId: string) =>
      row.kind === DOCUMENTS_TABLE_ROW_KIND.FOLDER &&
      isOrganizationScopedFolder(row.folder.scope) &&
      fileByRowId.get(draggedRowId)?.folderId !== row.id,
    [fileByRowId],
  );

  const { getMovableRowProps } = useMovableTableRows<DocumentsTableRow>({
    isRowDraggable,
    isRowDropTarget,
    onMove: handleMoveFile,
  });

  const { SkipLink, SkipLinkTarget } = useSkipWidgetLink();

  if (isLoading || isLoadingFiles) {
    return <Loading />;
  }

  return (
    <StyledMain>
      {documentFolders?.length ? (
        <>
          <SkipLink linkText="Skip to pagination" />
          <DocumentsTable
            ariaLabel="Documents folders list"
            columns={columns}
            rows={displayRows}
            getRowProps={getMovableRowProps}
            isPrefilteredAndNotEmpty
            makeTableFocusable
          />
          <SkipLinkTarget />
          <Pagination
            totalRowCount={filteredResults.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={setPage}
          />
        </>
      ) : (
        <EmptyState
          primaryText="There are no document folders configured for this organization."
          secondaryText={
            canEditOrgSettings
              ? 'Create a folder to start organizing your documents. Documents in all-sites folders apply to every site; documents in per-site folders are used at individual sites.'
              : 'Your organization admin can create folders to organize documents.'
          }
        />
      )}
    </StyledMain>
  );
}
